import { plus } from '@0xtorch/big-decimal'
import { type CryptoCurrency, type FiatCurrency, rest } from '@0xtorch/core'
import { z } from 'zod'
import type { AssetBalance } from '../../types'
import { createSha256Hash } from '../../utils/createSha256Hash'
import { getAsset } from '../../utils/getAsset'
import { binanceApiEndpoint, binanceSymbolCryptoIdMap } from '../constants'
import { fundingAssetSchema } from './schema'

const responseSchema = z.array(fundingAssetSchema)

const urlPath = '/sapi/v1/asset/get-funding-asset'

type FetchFundingAccountAssetsParameters = {
  apiKey: string
  apiSecret: string
  proxy?: string
  proxyHeaders?: Record<string, string>
  cryptoCurrencies: readonly CryptoCurrency[]
  fiatCurrencies: readonly FiatCurrency[]
}

export const fetchFundingAccountAssets = async ({
  apiKey,
  apiSecret,
  proxy,
  proxyHeaders,
  cryptoCurrencies,
  fiatCurrencies,
}: FetchFundingAccountAssetsParameters): Promise<readonly AssetBalance[]> => {
  const timestamp = Date.now()

  const url = new URL(urlPath, binanceApiEndpoint)
  url.searchParams.append('timestamp', timestamp.toString())
  url.searchParams.append(
    'signature',
    await createSha256Hash(url.searchParams.toString(), apiSecret),
  )

  const response = await rest(
    proxy === undefined
      ? url.toString()
      : `${proxy}${encodeURIComponent(url.toString())}`,
    {
      fetchOptions: {
        method: 'POST',
        headers: {
          'X-MBX-APIKEY': apiKey,
          ...proxyHeaders,
        },
      },
      schema: responseSchema,
    },
  )

  const assetBalances: AssetBalance[] = []
  for (const assetData of response) {
    const asset = getAsset({
      symbol: assetData.asset,
      cryptoCurrencies,
      fiatCurrencies,
      symbolCryptoIdMap: binanceSymbolCryptoIdMap,
    })
    if (asset === undefined) {
      continue
    }
    const amount = plus(assetData.free, assetData.locked)
    if (amount.value <= 0n) {
      continue
    }
    assetBalances.push({
      key: 'binance_funding',
      cryptoId: asset.type === 'CryptoCurrency' ? asset.id : undefined,
      fiatId: asset.type === 'FiatCurrency' ? asset.id : undefined,
      amount: plus(assetData.free, assetData.locked),
      timestamp,
    })
  }

  return assetBalances
}
